/**
 * AgencyAcceptInvite.jsx — public landing page for /agency/accept-invite/:token.
 * Mirrors StudioAcceptInvite but for agency_role + writes to the agency_invitations
 * collection.  On success → /agency.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { ArrowRight, Lock, ShieldCheck, UserCircle, Crown, Warning, Check, Buildings } from '@phosphor-icons/react';

const ACCENT = '#6366f1';

const RoleIcon = ({ role }) => {
  if (role === 'admin') return <Crown size={11} weight="fill" />;
  if (role === 'senior') return <ShieldCheck size={11} weight="fill" />;
  return <UserCircle size={11} weight="fill" />;
};

const AgencyAcceptInvite = ({ onLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/agency/invite/${token}/preview`);
        setPreview(r.data);
        setFullName(r.data.full_name || '');
      } catch {
        setPreview({ status: 'not_found' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!password || password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPw) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/agency/invite/${token}/accept`, { password, full_name: fullName });
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(r.data.user));
      onLogin?.(r.data.user, r.data.token);
      toast.success(`Welcome to ${preview.agency_name}!`);
      navigate('/agency', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept invite');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-sm text-text-muted">Loading…</div>;

  const isError = !preview || preview.status !== 'pending';
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="agency-accept-invite">
      <div className="w-full max-w-md bg-surface border border-border rounded-2xl p-7">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}>
            <Buildings size={16} weight="fill" className="text-white" />
          </span>
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>Founderboard</div>
            <div className="text-sm font-heading font-bold text-text-primary leading-none">Agency invite</div>
          </div>
        </div>

        {isError ? (
          <div className="text-sm text-text-secondary">
            <div className="flex items-center gap-2 text-warning mb-3"><Warning size={14} weight="bold" /> This invite is {preview?.status || 'not available'}.</div>
            <p className="text-text-muted">Ask the agency admin for a fresh invite.</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-heading font-bold text-text-primary mb-1" style={{ letterSpacing: '-0.02em' }}>Join {preview.agency_name}</h1>
            <p className="text-sm text-text-secondary mb-5"><strong>{preview.invited_by_name}</strong> invited you as <span className="inline-flex items-center gap-1 capitalize" style={{ color: ACCENT }}><RoleIcon role={preview.agency_role} /> {preview.agency_role}</span>.</p>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Email</label>
                <div className="text-sm font-mono text-text-primary px-3 py-2 bg-background border border-border rounded-lg" data-testid="invite-email">{preview.email}</div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Your name</label>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="invite-fullname" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Set password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Confirm password</label>
                <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="invite-confirm" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <button onClick={submit} disabled={busy} data-testid="invite-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                {busy ? 'Setting up…' : <>Accept invite <ArrowRight size={12} weight="bold" /></>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgencyAcceptInvite;
